import React from 'react';
import css from '@emotion/css';
import { PageInfo } from '../../../../libs/paging';
import { SortActionType } from '../../../../libs/filter';
import { formatIntNumber, formatDate, formatPercent } from '../../../../libs/format';
import { InstagramFeedPostSortField, ORDER } from '../../../../__generated__/globalTypes';
import * as List from '../../../atoms/List';
import CarouselModal from '../../../molecules/CarouselPopup';
import PostColumn from '../PostColumn';
import { SliderComponents, SliderCss, StyledComponents } from '../../../molecules/SliderTable';
import { usePostDetailModal } from '../../AnalyticsShared/BasePostList/usePostDetailModal';
import CompareFeedPostDetail from '../CompareFeedPostDetail';
import { getAllPostsHeaders } from '../postsHelpers';
import { AllInstagramCompareFeedPosts_allInstagramCompareFeedPosts } from './__generated__/AllInstagramCompareFeedPosts';

export type InstagramFeedPostsSortAction = SortActionType<InstagramFeedPostSortField>;

interface InstagramCompareFeedPostListProps extends InstagramFeedPostsSortAction {
  instagramFeedPosts: AllInstagramCompareFeedPosts_allInstagramCompareFeedPosts[];
  pageInfo: PageInfo;
}

const InstagramCompareFeedPostList = (props: InstagramCompareFeedPostListProps) => {
  const {
    sort: { field, order },
    setSort,
    instagramFeedPosts,
    pageInfo: { firstIndex, lastIndex, totalCount, currentPage, totalPages }
  } = props;

  const {
    openPostDetail,
    siblingPostId,
    handleClosePostDetail,
    handleClickPost,
    handleClickNext,
    handleClickPrev
  } = usePostDetailModal(instagramFeedPosts);

  return (
    <>
      <StyledComponents.ButtonWrapper css={SliderCss.boderTop}>
        <SliderComponents.PageCount total={totalCount} countLabel="Posts" />
      </StyledComponents.ButtonWrapper>
      <SliderComponents.Wrapper css={[SliderCss.marginTop, SliderCss.clearPadding]}>
        <SliderComponents.Container>
          <SliderComponents.SliderSection>
            <SliderComponents.SliderTable>
              <thead>
                <SliderComponents.HeaderRow css={SliderCss.bgndColor}>
                  <SliderComponents.StyckyHeaderDivWrapper>
                    <List.HeaderColumn
                      title="Post"
                      css={[SliderCss.clearPadding, SliderCss.width245, SliderCss.flexStart]}
                      isTable={false}
                    />
                  </SliderComponents.StyckyHeaderDivWrapper>
                  {/* slider */}
                  {getAllPostsHeaders(true).map(col => (
                    <List.HeaderColumn
                      key={col.columnName}
                      title={col.title}
                      help={col.help}
                      activeAsc={field === col.orderField && order === ORDER.ASC}
                      activeDesc={field === col.orderField && order === ORDER.DESC}
                      sortAsc={() => setSort({ field: col.orderField, order: ORDER.ASC })}
                      sortDesc={() => setSort({ field: col.orderField, order: ORDER.DESC })}
                      css={col.css}
                    />
                  ))}
                </SliderComponents.HeaderRow>
              </thead>
              <tbody>
                {instagramFeedPosts.map(item => {
                  const {
                    views,
                    engagementRate,
                    engagement,
                    likes,
                    comments,
                    positiveRate,
                    negativeRate,
                    postDate,
                    id,
                    thumbNail,
                    content
                  } = item;

                  return (
                    <StyledComponents.StyledRowNew key={id} css={[SliderCss.paddingSides, SliderCss.rowHeight62]}>
                      <SliderComponents.StickyBodyRowWrapper css={SliderCss.clearPadding}>
                        <PostColumn
                          key={id}
                          id={id}
                          src={thumbNail}
                          title={content}
                          onClick={() => handleClickPost(id)}
                        />
                      </SliderComponents.StickyBodyRowWrapper>
                      {/* sliding section */}
                      <StyledComponents.NumberColumn data={formatIntNumber(views)} />
                      <List.AmountColumn
                        data={formatPercent(engagementRate, false, 2)}
                        currency="%"
                        css={rateCurrencyFontSize}
                      />
                      <StyledComponents.NumberColumn data={formatIntNumber(engagement)} />
                      <StyledComponents.NumberColumn data={formatIntNumber(likes)} />
                      <StyledComponents.NumberColumn data={formatIntNumber(comments)} />
                      <List.AmountColumn
                        data={formatPercent(positiveRate, false, 2)}
                        currency="%"
                        css={rateCurrencyFontSize}
                      />
                      <List.AmountColumn
                        data={formatPercent(negativeRate, false, 2)}
                        currency="%"
                        css={rateCurrencyFontSize}
                      />
                      <List.TextColumn data={formatDate(postDate)} css={rightAlignedCol} />
                    </StyledComponents.StyledRowNew>
                  );
                })}
              </tbody>
            </SliderComponents.SliderTable>
          </SliderComponents.SliderSection>
        </SliderComponents.Container>
      </SliderComponents.Wrapper>
      <SliderComponents.Pager
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        first={firstIndex}
        last={lastIndex}
      />
      <CarouselModal
        open={!!openPostDetail}
        title=""
        cancel="OK"
        onClose={handleClosePostDetail}
        onClickNext={handleClickNext}
        onClickPrev={handleClickPrev}
        siblingPostId={siblingPostId}
      >
        <CompareFeedPostDetail closeModal={handleClosePostDetail} />
      </CarouselModal>
    </>
  );
};

const rateCurrencyFontSize = css`
  div > p {
    font-size: 12px;
  }
`;
const rightAlignedCol = css`
  & div {
    justify-content: flex-end;
  }
`;

export default InstagramCompareFeedPostList;
